'use client'

import { useState } from "react"
import { motion } from "framer-motion"
import { Send, MapPin, Phone, Mail, Clock, MessageSquare, Loader2, CheckCircle2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { toast } from "sonner"
import { SITE_CONFIG, RAYONS } from "@/lib/site-config"

export function ContactSection() {
  const [submitting, setSubmitting] = useState(false)
  const [sent, setSent] = useState(false)
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    rayon: "",
    subject: "",
    message: "",
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || "Erreur")
      toast.success("Message envoyé ! Nous vous répondrons rapidement.", {
        description: "Une copie a été transmise à l'équipe et un email de confirmation a été envoyé.",
      })
      setSent(true)
      setForm({ name: "", email: "", phone: "", rayon: "", subject: "", message: "" })
    } catch (e: any) {
      toast.error("Échec de l'envoi", { description: e.message })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section id="contact" className="py-20 md:py-28 relative overflow-hidden">
      <div className="absolute inset-x-0 top-0 h-px pattern-divider" />
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="font-display text-4xl md:text-6xl font-bold text-cuir mb-4">
            Nous <span className="text-gradient-gold">Contacter</span>
          </h2>
          <p className="text-foreground/70 max-w-2xl mx-auto">
            Une question, une demande de partenariat, une recherche de produit spécifique ?
            Écrivez-nous, notre équipe vous répondra avec plaisir.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 max-w-6xl mx-auto">
          {/* Contact info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-2 space-y-6"
          >
            <div className="gradient-emerald-gold rounded-xl p-6 md:p-8 text-primary-foreground relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1 gradient-gold" />
              <h3 className="font-display text-2xl mb-6">Informations pratiques</h3>
              <ul className="space-y-5">
                <li className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-primary-foreground/15 flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-5 w-5 text-gold" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-gold mb-1">Adresse</p>
                    <p className="text-sm leading-relaxed">
                      {SITE_CONFIG.address.line1}<br />
                      {SITE_CONFIG.address.line2}<br />
                      {SITE_CONFIG.address.zip} {SITE_CONFIG.address.city}<br />
                      <span className="text-primary-foreground/70">{SITE_CONFIG.address.metro}</span>
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-primary-foreground/15 flex items-center justify-center flex-shrink-0">
                    <Phone className="h-5 w-5 text-gold" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-gold mb-1">Téléphone</p>
                    <a href={`tel:${SITE_CONFIG.contact.phone.replace(/\s/g, "")}`} className="text-sm hover:text-gold">
                      {SITE_CONFIG.contact.phone}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-primary-foreground/15 flex items-center justify-center flex-shrink-0">
                    <Mail className="h-5 w-5 text-gold" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-gold mb-1">Email</p>
                    <a href={`mailto:${SITE_CONFIG.contact.email}`} className="text-sm hover:text-gold break-all">
                      {SITE_CONFIG.contact.email}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-primary-foreground/15 flex items-center justify-center flex-shrink-0">
                    <Clock className="h-5 w-5 text-gold" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-gold mb-1">Horaires</p>
                    <p className="text-sm">{SITE_CONFIG.contact.hours}</p>
                  </div>
                </li>
              </ul>
            </div>

            {/* Map embed */}
            <div className="rounded-xl overflow-hidden border border-gold/20 shadow-luxury">
              <iframe
                title="Localisation SADIYA STYLE CUIR"
                src="https://www.openstreetmap.org/export/embed.html?bbox=2.3681%2C48.8700%2C2.3810%2C48.8790&layer=mapnik&marker=48.8745%2C2.3745"
                className="w-full h-64 border-0"
                loading="lazy"
              />
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-3"
          >
            <div className="bg-card rounded-xl border border-gold/20 shadow-luxury p-6 md:p-8 relative">
              <div className="absolute top-0 left-0 right-0 h-1 gradient-gold rounded-t-xl" />

              {sent ? (
                <div className="text-center py-12">
                  <CheckCircle2 className="h-16 w-16 text-emerald mx-auto mb-4" />
                  <h3 className="font-display text-2xl text-cuir mb-2">Message envoyé !</h3>
                  <p className="text-foreground/70 mb-6">
                    Merci de votre confiance. Notre équipe vous répondra dans les plus brefs délais.
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => setSent(false)}
                    className="border-gold/40 text-cuir hover:bg-cuir hover:text-primary-foreground"
                  >
                    Envoyer un autre message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <MessageSquare className="h-5 w-5 text-gold" />
                    <h3 className="font-display text-2xl text-cuir">Formulaire de contact</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name" className="text-cuir">Nom complet *</Label>
                      <Input
                        id="name"
                        required
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="Votre nom"
                        className="mt-1 border-gold/30 focus-visible:ring-gold"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-cuir">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        required
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder="vous@email.com"
                        className="mt-1 border-gold/30 focus-visible:ring-gold"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone" className="text-cuir">Téléphone</Label>
                      <Input
                        id="phone"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        placeholder="+33 6 00 00 00 00"
                        className="mt-1 border-gold/30 focus-visible:ring-gold"
                      />
                    </div>
                    <div>
                      <Label htmlFor="rayon" className="text-cuir">Rayon concerné</Label>
                      <Select
                        value={form.rayon}
                        onValueChange={(v) => setForm({ ...form, rayon: v })}
                      >
                        <SelectTrigger className="mt-1 border-gold/30 focus-visible:ring-gold">
                          <SelectValue placeholder="Sélectionner un rayon" />
                        </SelectTrigger>
                        <SelectContent>
                          {RAYONS.map((r) => (
                            <SelectItem key={r.slug} value={r.slug}>
                              {r.name} — {r.subtitle}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="subject" className="text-cuir">Sujet *</Label>
                    <Input
                      id="subject"
                      required
                      value={form.subject}
                      onChange={(e) => setForm({ ...form, subject: e.target.value })}
                      placeholder="Objet de votre message"
                      className="mt-1 border-gold/30 focus-visible:ring-gold"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-cuir">Message *</Label>
                    <Textarea
                      id="message"
                      required
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      placeholder="Votre message…"
                      rows={5}
                      className="mt-1 border-gold/30 focus-visible:ring-gold resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full gradient-emerald-gold border border-gold/50 text-primary-foreground hover:opacity-90 h-12"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Envoi en cours…
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 h-4 w-4" />
                        Envoyer le message
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-foreground/50 text-center">
                    En soumettant ce formulaire, vous acceptez d'être recontacté par SADIYA STYLE CUIR.
                    Vos données ne seront jamais partagées avec des tiers.
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
